import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { apiRequest } from '../utils/api';
import {
  STATUS_OPTIONS, STATUS_STYLES,
  LM_BOX_STATUS_OPTIONS, LM_BOX_STATUS_STYLES,
} from '../utils/constants';
import { getCachedRecords, setCachedRecords } from '../utils/recordsCache';
import { PageHeader } from './ui';

// ── Dashboard-specific donut colors (hex needed for recharts) ─────────────────
const DONUT_COLORS = {
  "Pending":     "#F59E0B",
  "Sent":        "#16A34A",
  "Boxed":       "#0891B2",
  "Broken Item": "#E11D48",
  "Re-boxed":    "#0891B2",
  "Resent":      "#16A34A",
  "Hold":        "#2563EB",
  "Cancelled":   "#DC2626",
  "Data Check":  "#7C3AED",
};

const STATUS_CONFIG = STATUS_OPTIONS.map(key => ({
  key,
  label:  key,
  bg:     STATUS_STYLES[key]?.background  || "var(--color-surface-alt)",
  text:   STATUS_STYLES[key]?.color       || "var(--color-text-primary)",
  border: STATUS_STYLES[key]?.borderColor || "var(--color-border)",
  donut:  DONUT_COLORS[key]               || "#9CA3AF",
}));

const LM_BOX_CONFIG = LM_BOX_STATUS_OPTIONS.map(key => ({
  key,
  label:  key,
  bg:     LM_BOX_STATUS_STYLES[key]?.background  || "var(--color-surface-alt)",
  text:   LM_BOX_STATUS_STYLES[key]?.color       || "var(--color-text-primary)",
  border: LM_BOX_STATUS_STYLES[key]?.borderColor || "var(--color-border)",
}));

const TAG_CONFIG = [
  { key: 'Annual',       bg: 'var(--color-info-bg)',     text: 'var(--color-info)',           border: 'var(--color-info-border)'   },
  { key: 'Life',         bg: 'var(--color-orange-light)', text: 'var(--color-orange)',         border: 'var(--color-orange-muted)'  },
  { key: 'Gifted Annual',bg: 'var(--color-teal-bg)',     text: 'var(--color-teal)',           border: 'var(--color-teal-border)'   },
  { key: 'Gifted Life',  bg: 'var(--color-teal-bg)',     text: 'var(--color-teal)',           border: 'var(--color-teal-border)'   },
  { key: 'Gift',         bg: 'var(--color-surface-alt)', text: 'var(--color-text-secondary)', border: 'var(--color-border)'        },
];

const POLL_INTERVAL_MS = 3000;

function isLifeTag(tag) {
  return Boolean(tag) && tag.includes('Life');
}

// ── Custom donut tooltip ──────────────────────────────────────────────────────
function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      padding: '8px 12px',
      boxShadow: 'var(--shadow-md)',
      fontSize: '0.8rem',
    }}>
      <span style={{ color: p.donutColor, fontWeight: 600 }}>{name}</span>
      <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
        {value} record{value !== 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ── Stat card (still used for Membership tag cards, unchanged shape) ─────────
function StatCard({ label, count, total, bg, text, border, newCount }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 'var(--radius-xl)',
      padding: '20px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}>
      <span style={{ fontSize: '0.65rem', fontWeight: 700, color: text, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </span>
      <span style={{ fontSize: '2.25rem', fontWeight: 700, color: text, lineHeight: 1.1 }}>
        {count}
      </span>
      <span style={{ fontSize: '0.75rem', color: text, opacity: 0.7 }}>
        {pct}% of total
      </span>
      {newCount != null && (
        <span style={{ fontSize: '0.7rem', color: text, opacity: 0.6 }}>
          {newCount} new
        </span>
      )}
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children, withDivider }) {
  return (
    <div style={{
      fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.1em',
      marginTop: withDivider ? 4 : 0,
      paddingTop: withDivider ? 20 : 0,
      borderTop: withDivider ? '1px solid var(--color-border)' : 'none',
    }}>
      {children}
    </div>
  );
}

// ── DashCard — shared shell for the three insight widgets below the hero ──────
// Same white/border/shadow/radius language as every other card on this page,
// just a smaller padding scale since these hold compact widgets, not headline
// numbers. Fixed min-height keeps all three the same height regardless of
// which widget (checklist / bar / funnel) sits inside, so the row's edges
// align even though the content shapes differ.
function DashCard({ title, children }) {
  return (
    <div style={{
      background: 'white', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-xl)', padding: '18px 20px',
      boxShadow: 'var(--shadow-xs)', display: 'flex', flexDirection: 'column',
      gap: 14, minHeight: 148,
    }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {title}
      </span>
      {children}
    </div>
  );
}

// ── ProgressRow — one completion metric as a slim bar, not a giant number.
// Used for Letter Made / Interaction Logged: these are checklist items,
// not headline stats, so they get a form sized to their importance. ─────────
function ProgressRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>{label}</span>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>{count}</span> / {total} · {pct}%
        </span>
      </div>
      <div style={{ height: 8, background: 'var(--color-surface-alt)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 'var(--radius-full)', transition: 'width 400ms ease' }} />
      </div>
    </div>
  );
}

// ── SegmentedBar — Life Member Box's four states as proportion of one
// whole, not four competing numbers. A single bar answers "what's the
// overall picture" at a glance; the legend below still carries every
// raw count, so nothing from the original ask is lost, just reshaped. ───────
function SegmentedBar({ segments, total }) {
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 'var(--radius-full)', overflow: 'hidden', background: 'var(--color-surface-alt)' }}>
        {segments.map(s => {
          const pct = total > 0 ? (s.count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div key={s.label} title={`${s.label}: ${s.count}`}
              style={{ width: `${pct}%`, background: s.color, transition: 'width 400ms ease' }} />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px', marginTop: 10 }}>
        {segments.map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>{s.label}</span>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── BrokenItemFunnel — Broken → Re-boxed → Resent is a literal sequence,
// so it's drawn as one: connected stages, not three disconnected stats.
// A calm "nothing broken" state replaces a wall of zeros when there's
// genuinely nothing to report — the empty state is itself useful
// information (confirmation, not absence). ──────────────────────────────────
function BrokenItemFunnel({ broken, reboxed, resent }) {
  const hasAny = broken + reboxed + resent > 0;

  if (!hasAny) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-success)', flexShrink: 0 }} />
        No broken items right now
      </div>
    );
  }

  const steps = [
    { label: 'Broken',   count: broken,  bg: 'var(--status-brokenitem-bg)', text: 'var(--status-brokenitem-text)' },
    { label: 'Re-boxed', count: reboxed, bg: 'var(--status-boxed-bg)',      text: 'var(--status-boxed-text)'      },
    { label: 'Resent',   count: resent,  bg: 'var(--status-sent-bg)',       text: 'var(--status-sent-text)'       },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div style={{ flex: 1, background: s.bg, borderRadius: 'var(--radius-lg)', padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.text, lineHeight: 1 }}>{s.count}</div>
            <div style={{ fontSize: '0.65rem', fontWeight: 600, color: s.text, opacity: 0.8, marginTop: 2 }}>{s.label}</div>
          </div>
          {i < steps.length - 1 && (
            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', flexShrink: 0 }}>→</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Live pulse indicator ──────────────────────────────────────────────────────
function LiveBadge({ lastUpdated }) {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(lastUpdated);

  useEffect(() => {
    if (prevRef.current !== lastUpdated) {
      prevRef.current = lastUpdated;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
  }, [lastUpdated]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: 'var(--color-success)',
        display: 'inline-block',
        transition: 'box-shadow 200ms',
        boxShadow: flash ? '0 0 0 4px rgba(22,163,74,0.25)' : 'none',
      }} />
      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>
        Live
      </span>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function FulfillmentDashboard() {
  const [promotions,        setPromotions]        = useState([]);
  const [selectedPromoID,   setSelectedPromoID]   = useState('');
  const [selectedPromo,     setSelectedPromo]     = useState(null);
  const [allRecords,        setAllRecords]        = useState(() => getCachedRecords() || []);
  const [loadingPromotions, setLoadingPromotions] = useState(true);
  const [loadingPromo,      setLoadingPromo]      = useState(false);
  const [loadingRecords,    setLoadingRecords]    = useState(() => !getCachedRecords());
  const [error,             setError]             = useState(null);
  const [lastUpdated,       setLastUpdated]       = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    apiRequest('/promotions')
      .then(data => {
        const list   = Array.isArray(data) ? data : (data.promotions || []);
        const active = list.filter(p => p.IsActive && p.IsMembership);
        setPromotions(active);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoadingPromotions(false));
  }, []);

  useEffect(() => {
    if (!selectedPromoID) {
      setSelectedPromo(null);
      return;
    }
    setLoadingPromo(true);
    apiRequest(`/promotions/${selectedPromoID}`)
      .then(data => setSelectedPromo(data))
      .catch(err => setError(err.message))
      .finally(() => setLoadingPromo(false));
  }, [selectedPromoID]);

  const fetchRecords = useCallback(async () => {
    try {
      const data = await apiRequest('/fulfillment/records');
      const fetched = data.records || [];
      setAllRecords(fetched);
      setCachedRecords(fetched);
      setLastUpdated(Date.now());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
    intervalRef.current = setInterval(fetchRecords, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchRecords]);

  const selectedSourceCodes = new Set(
    (selectedPromo?.SourceCodes || []).map(sc => sc.SourceCode)
  );

  const scopedRecords = selectedPromoID && selectedPromo
    ? allRecords.filter(r => selectedSourceCodes.has(r.SourceCode))
    : allRecords;

  const records = scopedRecords.filter(r => r.IsMembership === true);


  const total = records.length;

  const counts = STATUS_CONFIG.reduce((acc, s) => {
    acc[s.key] = records.filter(r => (r.Status || 'Pending') === s.key).length;
    return acc;
  }, {});

  const membershipPromoNames = new Set(promotions.map(p => p.PromotionName));

  const membershipRecords = selectedPromoID
    ? records
    : records.filter(r => membershipPromoNames.has(r.PromotionName));

  const tagCounts = TAG_CONFIG.reduce((acc, t) => {
    acc[t.key] = membershipRecords.filter(r => r.TransactionTag === t.key).length;
    return acc;
  }, {});

  const tagNewCounts = TAG_CONFIG.reduce((acc, t) => {
    acc[t.key] = membershipRecords.filter(r => r.TransactionTag === t.key && r.IsNewMembership === true).length;
    return acc;
  }, {});

  const membershipTotal = Object.values(tagCounts).reduce((s, v) => s + v, 0);

  // Boxed / Sent are cumulative funnel stages (see prior session notes):
  // reaching a later stage implies every earlier one already happened.
  const boxedCount  = (counts['Boxed'] || 0) + (counts['Re-boxed'] || 0);
  const sentCount   = (counts['Resent'] || 0) + (counts['Sent'] || 0);
  const completePct = total > 0 ? Math.round((sentCount / total) * 100) : 0;

  const letterMadeCount        = records.filter(r => Boolean(r.LetterMade)).length;
  const interactionLoggedCount = records.filter(r => Boolean(r.TEDInteractionLogged)).length;

  const lifeTagRecords = membershipRecords.filter(r => isLifeTag(r.TransactionTag));
  const lifeBoxTotal   = lifeTagRecords.length;
  const lifeBoxCounts  = LM_BOX_CONFIG.reduce((acc, s) => {
    acc[s.key] = lifeTagRecords.filter(r => (r.LifeMemberBoxStatus || 'Pending') === s.key).length;
    return acc;
  }, {});

  const brokenCount  = counts['Broken Item'] || 0;
  const reboxedCount = counts['Re-boxed']    || 0;
  const resentCount  = (counts['Resent'] || 0);

  const donutData = STATUS_CONFIG
    .map(s => ({ name: s.label, value: counts[s.key], donutColor: s.donut }))
    .filter(d => d.value > 0);

  const isLoading = loadingPromotions || loadingRecords || loadingPromo;

  if (loadingPromotions) {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-white">
        <div className="px-6 sm:px-8 py-5 border-b border-[var(--color-border)] shrink-0">
          <div className="h-5 w-48 rounded-[var(--radius-md)] bg-[var(--color-surface-alt)] animate-pulse mb-1.5" />
          <div className="h-3 w-32 rounded-[var(--radius-md)] bg-[var(--color-surface-alt)] animate-pulse" />
        </div>
        <div className="flex-1 p-6 grid grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 rounded-[var(--radius-xl)] bg-[var(--color-surface-alt)] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">

      <PageHeader
        title="Fulfillment Dashboard"
        subtitle={selectedPromo ? selectedPromo.PromotionName : 'All active membership promotions'}
        action={<LiveBadge lastUpdated={lastUpdated} />}
      />

      {/* Toolbar */}
      <div style={{
        padding: '12px 28px',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
      }}>
        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
          Promotion
        </label>
        <select
          value={selectedPromoID}
          onChange={e => setSelectedPromoID(e.target.value)}
          style={{
            height: 36, padding: '0 12px',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            background: 'white', fontSize: '0.875rem',
            color: 'var(--color-text-primary)',
            outline: 'none', minWidth: 240,
          }}
        >
          <option value="">All Promotions</option>
          {promotions.map(p => (
            <option key={p.PromotionID} value={p.PromotionID}>{p.PromotionName}</option>
          ))}
        </select>

        {loadingPromo && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Loading…</span>
        )}

        {error && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-error)' }}>⚠ {error}</span>
        )}

        <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
          {loadingRecords ? 'Loading…' : `${total} record${total !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', background: 'var(--color-surface)', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {total === 0 && !isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8 }}>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>No records found for this promotion.</p>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>Check that source codes are linked in Promotions Manager.</p>
          </div>
        ) : (
          <>
            {/* ═══════════════════════ FULFILLMENT ═══════════════════════ */}
            <SectionLabel>Fulfillment</SectionLabel>

            {/* Hero + Donut */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>

              <div style={{
                background: completePct === 100 ? 'var(--status-sent-bg)' : 'var(--color-navy)',
                border: completePct === 100 ? '1px solid var(--status-sent-border)' : 'none',
                borderRadius: 'var(--radius-xl)',
                padding: '36px 40px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 24, boxShadow: completePct === 100 ? 'none' : 'var(--shadow-md)',
                transition: 'background 400ms ease', minHeight: 180,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <p style={{
                    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: completePct === 100 ? 'var(--status-sent-text)' : 'rgba(255,255,255,0.5)',
                    marginBottom: 0,
                  }}>
                    {completePct === 100 ? '🎉 All done!' : 'Boxed / Sent'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 32 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: completePct === 100 ? 'var(--status-sent-text)' : 'rgba(255,255,255,0.5)' }}>
                        Boxed
                      </span>
                      <span style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1, color: completePct === 100 ? 'var(--status-sent-text)' : 'white' }}>
                        {boxedCount}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: completePct === 100 ? 'var(--status-sent-text)' : 'rgba(255,255,255,0.5)' }}>
                        Sent
                      </span>
                      <span style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1, color: completePct === 100 ? 'var(--status-sent-text)' : 'white' }}>
                        {sentCount}
                      </span>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.8rem', color: completePct === 100 ? 'var(--status-sent-text)' : 'rgba(255,255,255,0.6)', opacity: 0.8 }}>
                    of {total} total records
                  </span>
                </div>
                <div style={{
                  fontSize: '6rem', fontWeight: 800, lineHeight: 1,
                  color: completePct === 100 ? 'var(--status-sent-text)' : 'white',
                  opacity: 0.95, textAlign: 'right', flexShrink: 0,
                }}>
                  {completePct}%
                </div>
              </div>

              <div style={{
                background: 'white', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-xl)', padding: '20px',
                boxShadow: 'var(--shadow-xs)', display: 'flex',
                flexDirection: 'column', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData.length > 0 ? donutData : [{ name: 'No data', value: 1 }]}
                        cx="50%" cy="50%"
                        innerRadius={52} outerRadius={72}
                        paddingAngle={donutData.length > 1 ? 3 : 0}
                        dataKey="value" strokeWidth={0}
                      >
                        {donutData.length > 0
                          ? donutData.map((entry, i) => <Cell key={i} fill={entry.donutColor} />)
                          : <Cell fill="var(--color-border)" />
                        }
                      </Pie>
                      {donutData.length > 0 && <Tooltip content={<DonutTooltip />} />}
                      {donutData.length > 0 && (
                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
                          <tspan x="50%" dy="-0.2em" style={{ fontSize: '1.4rem', fontWeight: 700, fill: 'var(--color-text-primary)' }}>
                            {total}
                          </tspan>
                          <tspan x="50%" dy="1.4em" style={{ fontSize: '0.6rem', fontWeight: 600, fill: 'var(--color-text-muted)', letterSpacing: '0.08em' }}>
                            TOTAL
                          </tspan>
                        </text>
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {STATUS_CONFIG.map(s => {
                    const count = counts[s.key];
                    if (count === 0) return null;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: s.donut, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', flex: 1 }}>{s.label}</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>{count}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', width: 32, textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Three insight widgets — one row, matching heights, each
                shaped to what it's actually showing rather than forced
                into the same generic stat-card mold. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>

              <DashCard title="Completion Checklist">
                <ProgressRow label="Letter Made" count={letterMadeCount} total={total} color="var(--color-teal)" />
                <ProgressRow label="Interaction Logged" count={interactionLoggedCount} total={total} color="var(--color-info)" />
              </DashCard>

              <DashCard title="Life Member Box">
                <SegmentedBar
                  total={lifeBoxTotal}
                  segments={LM_BOX_CONFIG.map(s => ({
                    label: s.label,
                    count: lifeBoxCounts[s.key],
                    color: s.text,
                  }))}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                  {lifeBoxTotal} Life-family record{lifeBoxTotal !== 1 ? 's' : ''}
                </span>
              </DashCard>

              <DashCard title="Broken Item Flow">
                <BrokenItemFunnel broken={brokenCount} reboxed={reboxedCount} resent={resentCount} />
              </DashCard>

            </div>

            {/* ═══════════════════════ MEMBERSHIP ═══════════════════════ */}
            <SectionLabel withDivider>Membership</SectionLabel>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
              {TAG_CONFIG.map(t => (
                <StatCard
                  key={t.key}
                  label={t.key}
                  count={tagCounts[t.key]}
                  total={membershipTotal}
                  bg={t.bg}
                  text={t.text}
                  border={t.border}
                  newCount={tagNewCounts[t.key]}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}